import { useContext } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';
import {
  CrmBoardKind, CrmContactsPage, CrmKind, CrmLead, CrmProspectChoice, CrmProspectResult, CrmVendorChoice,
} from '../models/crm.model';

export interface CrmContactsQuery {
  q?: string; source?: string; campaign?: string; status?: string; country?: string; page?: number;
}

/**
 * Internal sales CRM. The API only answers allowlisted super-admins and 404s
 * everyone else, so `checkAccess` failing simply means "not for you".
 */
export const useCrmApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);

  const checkAccess = (): Promise<{ allowed: boolean }> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.crm.access) as Promise<{ allowed: boolean }>;

  const list = <T>(kind: Exclude<CrmKind, 'contacts'>): Promise<T[]> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.crm.list(kind)) as Promise<T[]>;

  const listContacts = (query: CrmContactsQuery): Promise<CrmContactsPage> => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    return sendAuthGuardedRequest(ApiMethod.GET, qs ? `${routes.crm.contacts}?${qs}` : routes.crm.contacts) as Promise<CrmContactsPage>;
  };

  const create = <T>(kind: CrmKind, data: Partial<T>): Promise<T> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.crm.list(kind), data) as Promise<T>;

  const update = <T>(kind: CrmKind, id: string, data: Partial<T>): Promise<T> =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.crm.record(kind, id), data) as Promise<T>;

  const remove = (kind: CrmKind, id: string): Promise<{ ok: boolean }> =>
    sendAuthGuardedRequest(ApiMethod.DELETE, routes.crm.record(kind, id)) as Promise<{ ok: boolean }>;

  // Persist a column's new manual order (and the dragged card's new stage).
  const reorder = (kind: CrmBoardKind, items: { id: string; sort: number; stage?: string }[]) =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.crm.reorder(kind), { items });

  // Give a lead its vendor: an existing one, or a new prospect. A vendor linked to another lead is refused.
  const toProspect = (leadId: string, choice: CrmProspectChoice): Promise<CrmProspectResult> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.crm.toProspect(leadId), choice) as Promise<CrmProspectResult>;

  // Take a lead's vendor off it (the vendor itself stays).
  const unlinkVendor = (leadId: string): Promise<CrmLead> =>
    sendAuthGuardedRequest(ApiMethod.DELETE, routes.crm.leadVendor(leadId)) as Promise<CrmLead>;

  // Vendors to link a lead to, by name or alias, each with the lead it already belongs to.
  const searchVendors = (q: string): Promise<CrmVendorChoice[]> =>
    sendAuthGuardedRequest(ApiMethod.GET, q ? `${routes.crm.vendors}?q=${encodeURIComponent(q)}` : routes.crm.vendors) as Promise<CrmVendorChoice[]>;

  // One-shot migration from the standalone ardealy-crm export (upserts on the old ids).
  const importLegacy = (payload: Record<string, unknown>): Promise<Record<string, { upserted: number; skipped: string[] }>> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.crm.import, payload) as Promise<Record<string, { upserted: number; skipped: string[] }>>;

  return {
    checkAccess, list, listContacts, create, update, remove, reorder, toProspect, unlinkVendor, searchVendors, importLegacy,
  };
};
